import { createColumnHelper } from '@tanstack/react-table';
import { Bin, EllipsisMenu, IOption, Table, ViewIcon } from '@weareopenr/component-library';
import { useCallback, useMemo } from 'react';
import { capitalise } from 'src/helpers/capitalise';
import { NO_INVENTORY_TYPES } from 'src/constants';
import { isProductSyncActive } from 'src/helpers/isProductSyncActive/isProductSyncActive';
import { useNavigate } from 'react-router-dom';
import { Range } from '@weareopenr/types';
import styles from './ProductLibraryTable.module.scss';
import { useExternalSystems } from 'src/hooks/useExternalSystems';

export type ProductTableRow = {
  name: string;
  type: string | null;
  ranges: Range[];
  item_id: string | null;
  portion?: string | undefined;
  product_id?: string | null | undefined;
  product_sizes?: ProductTableRow[];
};

const columnHelper = createColumnHelper<ProductTableRow>();

const staticColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('portion', {
    header: 'Portion',
    size: 200,
  }),
  columnHelper.accessor('ranges', {
    header: 'Range',
    cell: (info) => {
      const ranges = info.getValue() || [];
      return ranges.map(({ name }) => name).join(',') || '-';
    },
    size: 200,
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: (info) => (info.getValue() ? capitalise(info.getValue()) : '-'),
    size: 150,
  }),
];

const isItemDeletable = (isSyncActive: boolean, itemType: string) => {
  if (!isSyncActive) {
    return true;
  } else if (isSyncActive && NO_INVENTORY_TYPES.includes(itemType)) {
    return true;
  } else {
    return false;
  }
};

const createOptions = (
  isSyncActive: boolean,
  menuItem: ProductTableRow,
  onView: (menuItem: ProductTableRow) => void,
  onDelete: (menuItem: ProductTableRow) => void,
): IOption[] => {
  const viewOption = { id: 1, name: 'View', icon: ViewIcon, onOptionClick: () => onView(menuItem) };
  if (isItemDeletable(isSyncActive, menuItem.type)) {
    return [
      viewOption,
      { id: 2, name: 'Delete', icon: Bin, onOptionClick: () => onDelete(menuItem), hoverColour: 'tomato' },
    ];
  }
  return [viewOption];
};

interface ProductLibraryTableProps {
  data: ProductTableRow[];
  onDelete: (menuItem: ProductTableRow) => void;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
}

export const ProductLibraryTable = ({
  data,
  onDelete,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ProductLibraryTableProps) => {
  const { data: externalSystems } = useExternalSystems();

  const isSyncActive = isProductSyncActive(externalSystems);

  const navigate = useNavigate();
  const onView = useCallback(({ item_id }: ProductTableRow): void => navigate(`/items/${item_id}`), [navigate]);

  const columns = useMemo(
    () => [
      ...staticColumns,
      columnHelper.display({
        id: 'actions',
        size: 80,
        cell: ({ row }) =>
          row.original.item_id && (
            <div className={styles['ellipsis-menu-container']} onClick={(e) => e.stopPropagation()}>
              <EllipsisMenu
                options={createOptions(isSyncActive, row.original, onView, onDelete)}
                variant="ghost"
                hoverStyle="high-contrast"
              />
            </div>
          ),
      }),
    ],
    [isSyncActive, onView, onDelete],
  );

  return (
    <Table
      className={styles['product-library-table']}
      data={data}
      columns={columns}
      isRowClickable={({ item_id }) => (item_id ? onView : false)}
      getSubRows={(originalRow) => originalRow.product_sizes}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
    />
  );
};
